import React, { useMemo, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faTimes } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";

const MarketGroupsTable = ({
  marketGroups,
  onSort,
  onRowClick,
  onPaginationChanged,
  paginationState,
}) => {
  const { t } = useTranslation(["markets"]);
  const gridRef = useRef();

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "name",
        headerName: t("Name"),
        sortable: true,
        filter: true,
        // Names typically need more space
        flex: 1,
      },
      {
        field: "active",
        headerName: t("Active"),
        sortable: true,
        filter: true,
        flex: 1,
        headerClass: "ag-center-aligned-header",
        cellRenderer: (params) => {
          const isActive = params.data?.active;
          return (
            <div className="centered" style={{ width: "100%", height: "100%" }}>
              <FontAwesomeIcon
                icon={isActive ? faCheck : faTimes}
                className={isActive ? "text-success" : "text-danger"}
                style={{ fontSize: "16px" }}
              />
            </div>
          );
        },
      },
    ],
    [t]
  );

  // Memoize default column properties
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      minWidth: 100,
      sortable: true,
      filter: true,
    }),
    []
  );

  // Handle sort changed event
  const handleSortChanged = useCallback(
    (event) => {
      if (!onSort) return;

      const { api } = event;
      const columnState = api.getColumnState();

      if (columnState && columnState.length > 0) {
        const sortedColumn = columnState.find((col) => col.sort);
        if (sortedColumn) {
          onSort(sortedColumn.colId, sortedColumn.sort);
        }
      } else {
        onSort(null, null);
      }
    },
    [onSort]
  );

  // Handle pagination changes
  const handlePaginationChanged = useCallback(
    (params) => {
      if (onPaginationChanged && params) {
        onPaginationChanged(params);
      }
    },
    [onPaginationChanged]
  );

  // Grid ready handler
  const onGridReady = useCallback(
    (params) => {
      gridRef.current = params;

      // Set current page from external state if available
      if (paginationState && paginationState.currentPage >= 0) {
        setTimeout(() => {
          params.api.paginationGoToPage(paginationState.currentPage);
        }, 0);
      }
    },
    [paginationState]
  );

  // Pagination configuration based on external state
  const paginationConfig = useMemo(
    () => ({
      // Enable pagination for all tables regardless of size
      enabled: true,
      // Use the page size from props or default
      pageSize: paginationState?.pageSize || 20,
      // Available page size options for user to choose
      pageSizeOptions: [5, 10, 20, 50, 100],
    }),
    [paginationState?.pageSize]
  );

  return (
    <ThemeableAgGrid
      className="table-bold clickable"
      columnDefs={columnDefs}
      defaultColDef={defaultColDef}
      rowData={marketGroups}
      onSortChanged={handleSortChanged}
      onRowClicked={(params) => onRowClick && onRowClick(params.data)}
      onPaginationChanged={handlePaginationChanged}
      onGridReady={onGridReady}
      getRowId={(params) => params.data.marketGroupId.toString()}
      animateRows={true}
      // Always use normal layout
      domLayout="autoHeight"
      // Enable pagination
      pagination={true}
      // Configure pagination settings
      paginationPageSize={paginationConfig.pageSize}
      paginationPageSizeSelector={paginationConfig.pageSizeOptions}
      // Enable panel to show entries info
      suppressPaginationPanel={false}
    />
  );
};

export default React.memo(MarketGroupsTable);

